import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const FONTS = ["Inter", "IBM Plex Sans", "Söhne", "Geist", "Söhne Mono"];

export default function Branding() {
    const { organisation, setOrganisation } = useAuth();
    const [branding, setBranding] = useState(organisation?.branding);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    if (!branding) return null;

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/org/current", { branding });
            setOrganisation(data);
            setBranding(data.branding);
            toast.success("Branding updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const uploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/org/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setOrganisation(data);
            setBranding(data.branding);
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid="branding-page" className="space-y-10">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                    Admin
                </div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                    Branding
                </h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    Colours and logo used in the admin shell and generated training content.
                </p>
            </div>

            <section className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="text-sm font-semibold text-neutral-900">Logo</h2>
                <div className="mt-4 flex items-center gap-6">
                    <div
                        data-testid="logo-preview"
                        className="flex h-20 w-20 items-center justify-center rounded-md border border-neutral-200 bg-neutral-50"
                    >
                        {branding.logo_signed_url ? (
                            <img
                                src={branding.logo_signed_url}
                                alt="logo"
                                className="max-h-16 max-w-16 object-contain"
                            />
                        ) : (
                            <span className="text-xs text-neutral-400">No logo</span>
                        )}
                    </div>
                    <div>
                        <input
                            ref={fileRef}
                            data-testid="logo-file-input"
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            onChange={uploadLogo}
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            data-testid="logo-upload-btn"
                        >
                            <UploadCloud size={15} className="mr-1.5" />
                            Upload logo
                        </Button>
                        <div className="mt-2 text-[11px] text-neutral-500">
                            PNG, JPEG, SVG or WebP. Up to 2 MB.
                        </div>
                    </div>
                </div>
            </section>

            <section className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="text-sm font-semibold text-neutral-900">Colours</h2>
                <div className="mt-4 grid max-w-2xl grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Primary</Label>
                        <div className="flex items-center gap-3">
                            <input
                                data-testid="primary-color-input"
                                type="color"
                                value={branding.primary_color}
                                onChange={(e) =>
                                    setBranding({ ...branding, primary_color: e.target.value })
                                }
                                className="h-10 w-14 cursor-pointer rounded-md border border-neutral-200 bg-white"
                            />
                            <Input
                                value={branding.primary_color}
                                onChange={(e) =>
                                    setBranding({ ...branding, primary_color: e.target.value })
                                }
                                className="mono"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Accent</Label>
                        <div className="flex items-center gap-3">
                            <input
                                data-testid="accent-color-input"
                                type="color"
                                value={branding.accent_color}
                                onChange={(e) =>
                                    setBranding({ ...branding, accent_color: e.target.value })
                                }
                                className="h-10 w-14 cursor-pointer rounded-md border border-neutral-200 bg-white"
                            />
                            <Input
                                value={branding.accent_color}
                                onChange={(e) =>
                                    setBranding({ ...branding, accent_color: e.target.value })
                                }
                                className="mono"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Font family</Label>
                        <select
                            data-testid="font-select"
                            value={branding.font}
                            onChange={(e) => setBranding({ ...branding, font: e.target.value })}
                            className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
                        >
                            {FONTS.map((f) => (
                                <option key={f} value={f}>
                                    {f}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-6">
                    <Button
                        onClick={save}
                        disabled={busy}
                        data-testid="branding-save"
                        className="bg-neutral-900 text-white hover:bg-neutral-800"
                    >
                        Save branding
                    </Button>
                </div>
            </section>
        </div>
    );
}
